"use client";

import { useLazyQuery } from "@apollo/client";
import { ICONS_MODULES_PATHS } from "components/ui/v2/sidebar/sidebar-icon-modules-path";
import SVG from "components/SVG";
import {
  DetailPageShell,
  LiquidPill,
  LiquidPillGroup,
  type LiquidPillGroupItem,
  Modal,
} from "components/ui/v2";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { useVendor } from "context/vendor/vendor.provider";
import { gqlUser } from "gql";
import type { UserVendorInfoRes } from "gql/User/queries";
import {
  ArrowLeft,
  BadgeCheck,
  Brain,
  Languages as LanguagesIcon,
  UserRound,
} from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PRIVATE_ROUTES } from "routes/routes";
import type { IAppreciationDistribute } from "types/Appreciation/AppreciationDistribute.type";
import { ParamsKey } from "types/Params/Params.type";
import type { ISkill } from "types/Skills.type";
import type { IUser } from "types/User.types";
import type { IUserVendorListType } from "types/User.types";
import { firsLetterCapitalize } from "utils/string";
import { EndorsementReviewDrawerContentV2 } from "./endorsement/EndorsementReviewDrawerContentV2";
import { NewEndorsementModalContentV2 } from "./endorsement/NewEndorsementModalContentV2";
import { RequestEndorsementModalContentV2 } from "./endorsement/RequestEndorsementModalContentV2";
import { PublicProfileMarketingCtaV2 } from "./PublicProfileMarketingCtaV2";
import { PublicProfileSidebarV2 } from "./PublicProfileSidebarV2";
import { PublicProfileTabPanelV2 } from "./PublicProfileTabPanelV2";
import { PublicProfileTab } from "./public-profile.constants";

export interface PublicProfilePageV2Props {
  user: IUser;
}

export function PublicProfilePageV2({ user }: PublicProfilePageV2Props) {
  const router = useRouter();
  const { query, push } = router;
  const { t } = useTranslation("common");
  const { vendor: vendorContext } = useVendor();
  const { user: currentUser } = useUserStoreContext((store) => ({
    user: store.user,
  }));

  const profileUserId = (query.id as string) || user.id;
  const queryVendorId = query.vendorId as string | undefined;

  const [selectedVendor, setSelectedVendor] = useState("");
  const [activeTab, setActiveTab] = useState<string>(PublicProfileTab.Endorsement);
  const [showingAppreciation, setShowingAppreciation] =
    useState<IAppreciationDistribute>();
  const [skillCount, setSkillCount] = useState(user.skills?.length ?? 0);

  const [reviewSkill, setReviewSkill] = useState<ISkill | null>(null);
  const [shareSkill, setShareSkill] = useState<ISkill | null>(null);
  const [validateTarget, setValidateTarget] = useState<{
    skillId: string;
    skillName: string;
  } | null>(null);

  const [
    userVendorInfo,
    { data: userVendorInfoData, loading: loadingUserVendorData },
  ] = useLazyQuery<UserVendorInfoRes>(gqlUser.queries.USER_VENDOR_INFO, {
    onCompleted: ({ userVendorInfo: info }) => {
      setShowingAppreciation(info?.lastAppreciation);
    },
  });

  const availableVendors = useMemo(
    () =>
      currentUser?.vendorList?.filter((vendor) =>
        user.vendorList?.map((shared) => shared.id).includes(vendor.id)
      ) || [],
    [currentUser?.vendorList, user.vendorList]
  );

  const isOwnProfile = currentUser?.id === profileUserId;
  const canShare = Boolean(
    isOwnProfile && currentUser?.selectedVendor?.enableExplore
  );

  const handleUserVendorInfo = useCallback(
    (vendorId: string) => {
      setSelectedVendor(vendorId);
      userVendorInfo({
        variables: {
          userId: user.id,
          vendorId,
        },
      });
    },
    [user.id, userVendorInfo]
  );

  useEffect(() => {
    const tab = (query.tab as string) || (query[ParamsKey.mtab] as string);
    if (tab) {
      setActiveTab(tab);
    }
  }, [query.tab, query[ParamsKey.mtab]]);

  useEffect(() => {
    if (selectedVendor) return;
    if (queryVendorId) {
      handleUserVendorInfo(queryVendorId);
      return;
    }
    if (availableVendors.length === 1) {
      handleUserVendorInfo(availableVendors[0].id);
    }
  }, [availableVendors, handleUserVendorInfo, queryVendorId, selectedVendor]);

  const profileTabs = (vendor?: { enableAdSync?: boolean; enableCorporateValue?: boolean }) => [
    {
      value: PublicProfileTab.Endorsement,
      label: t("Habilidades"),
      icon: ICONS_MODULES_PATHS.CONFIG.SKILLS,
      render: true,
    },
    {
      value: PublicProfileTab.Appreciation,
      label: t("Evaluaciones"),
      icon: ICONS_MODULES_PATHS.LEARNING.EVALUATIONS_MODULE,
      render: true,
    },
    {
      value: PublicProfileTab.Certifications,
      label: t("Certificaciones"),
      icon: ICONS_MODULES_PATHS.CONFIG.CERTIFICATE,
      render: true,
    },
    {
      value: PublicProfileTab.Projects,
      label: t("Proyectos"),
      icon: ICONS_MODULES_PATHS.CONFIG.GOALS,
      render: vendor?.enableAdSync,
    },
    {
      value: PublicProfileTab.Recognitions,
      label: t("Valores"),
      icon: ICONS_MODULES_PATHS.CONFIG.CORPORATE_VALUES,
      render: vendor?.enableCorporateValue,
    },
  ];

  const handleTabChange = (value: string) => {
    push(
      {
        pathname: router.pathname,
        query: { ...query, tab: value.toLowerCase() },
      },
      undefined,
      { shallow: true }
    );
    setActiveTab(value);
  };

  const fullName = `${firsLetterCapitalize(user.name)} ${firsLetterCapitalize(user.lastname || "")}`;

  return (
    <>
      <DetailPageShell
        header={{
          leadingIcon: (
            <UserRound className="h-10 w-10 shrink-0 text-indigo-600 dark:text-indigo-300" />
          ),
          title: fullName,
          description:
            user.shortBio ||
            t("Conoce el perfil del colaborador, sus habilidades, proyectos y mucho mas"),
          particleIcon: Brain,
          badges: (
            <>
              <LiquidPill
                variant="indigo"
                appearance="tinted"
                size="sm"
                icon={<BadgeCheck className="h-4 w-4" />}
                text={
                  userVendorInfoData?.userVendorInfo?.seniority
                    ? String(userVendorInfoData.userVendorInfo.seniority)
                    : t("Sin seniority asignado")
                }
              />
              <LiquidPill
                variant="indigo"
                appearance="tinted"
                size="sm"
                icon={<Brain className="h-4 w-4" />}
                text={
                  <>
                    <span className="font-semibold text-current">{skillCount}</span>{" "}
                    {t("habilidades")}
                  </>
                }
              />
              <LiquidPill
                variant="indigo"
                appearance="tinted"
                size="sm"
                icon={<LanguagesIcon className="h-4 w-4" />}
                text={
                  userVendorInfoData?.userVendorInfo?.englishLevel ||
                  t("Nivel de inglés no asignado")
                }
              />
            </>
          ),
        }}
        loading={loadingUserVendorData && !userVendorInfoData}
        loadingTitle={t("Cargando")}
        loadingDescription={t("Espere por favor")}
        toolbar={
          userVendorInfoData ? (
            <LiquidPillGroup
              items={profileTabs(vendorContext || undefined)
                .filter((tab) => tab.render !== false)
                .map(
                  (tab): LiquidPillGroupItem => ({
                    value: tab.value,
                    label: tab.label,
                    icon: <SVG src={tab.icon} width={14} height={14} />,
                  })
                )}
              activeValue={activeTab}
              onChange={handleTabChange}
              size="sm"
            />
          ) : undefined
        }
        overlays={
          <>
            {reviewSkill ? (
              <Modal
                open
                onClose={() => setReviewSkill(null)}
                size="lg"
                eyebrow={t("Perfil")}
                title={t("Valoraciones de")}
                description={reviewSkill.name || ""}
                panelClassName="max-h-[min(88vh,720px)]"
                headerClassName="px-4 py-2.5 sm:px-5 sm:py-3"
                bodyClassName="px-4 py-3 sm:px-5 sm:py-3.5"
              >
                <EndorsementReviewDrawerContentV2
                  userId={profileUserId}
                  skillId={reviewSkill.id}
                  skillName={reviewSkill.name || ""}
                />
              </Modal>
            ) : null}

            {shareSkill ? (
              <Modal
                open
                onClose={() => setShareSkill(null)}
                size="md"
                eyebrow={t("Perfil")}
                title={t("Solicitar validación")}
                bodyClassName="px-4 py-4 sm:px-5 sm:py-5"
              >
                <RequestEndorsementModalContentV2
                  skill={shareSkill}
                  onClose={() => setShareSkill(null)}
                />
              </Modal>
            ) : null}

            {validateTarget ? (
              <Modal
                open
                onClose={() => setValidateTarget(null)}
                size="md"
                eyebrow={t("Perfil")}
                title={t("Agregar Validación")}
                description={t("Estas validando la habilidad de {{name}}", {
                  name: user.name,
                })}
                bodyClassName="px-4 py-4 sm:px-5 sm:py-5"
              >
                <NewEndorsementModalContentV2
                  userId={profileUserId}
                  skillId={validateTarget.skillId}
                  name={validateTarget.skillName}
                  onClose={() => setValidateTarget(null)}
                />
              </Modal>
            ) : null}
          </>
        }
      >
        <LiquidPill
          as="button"
          type="button"
          variant="slate"
          appearance="tinted"
          shadow="neutral"
          size="sm"
          icon={<ArrowLeft className="h-4 w-4" />}
          text={t("Volver a Groowcity")}
          onClick={() => router.push(PRIVATE_ROUTES.profile.path)}
        />

        <div className="grid gap-6 lg:grid-cols-[minmax(0,32%)_minmax(0,68%)]">
          <PublicProfileSidebarV2
            user={user}
            userVendorInfo={userVendorInfoData?.userVendorInfo}
            availableVendors={availableVendors}
            selectedVendorId={selectedVendor}
            onVendorChange={handleUserVendorInfo}
          />

          {userVendorInfoData ? (
            <PublicProfileTabPanelV2
              activeTab={activeTab}
              user={user}
              userId={profileUserId}
              selectedVendorId={selectedVendor}
              userVendorInfo={userVendorInfoData.userVendorInfo}
              showingAppreciation={showingAppreciation}
              onAppreciationChange={setShowingAppreciation}
              isOwnProfile={isOwnProfile}
              canShare={canShare}
              onEndorsementReview={setReviewSkill}
              onEndorsementValidate={(skillId, skillName) =>
                setValidateTarget({ skillId, skillName })
              }
              onEndorsementShare={setShareSkill}
              onSkillCountChange={setSkillCount}
            />
          ) : null}
        </div>

        <PublicProfileMarketingCtaV2 />

        <p className="py-2 text-center text-sm text-slate-500 dark:text-zinc-400">
          {t("PROFILE_FOOTER")}
        </p>
      </DetailPageShell>
    </>
  );
}
